import Vue from "vue";

import { isNullish } from "@tinyurldev/tu-ui-vue";

import SUBSCRIPTION_TYPE from "@Constants/subscription-type";
import { OVERAGE_STATUS } from "@Constants/overage-status";

import { getApiUrl } from "@Helpers/api/buildUrl";
import BillableSubscription from "@Helpers/subscription";
import { XTeamsStore } from "../../modules/teams/Store/XTeamsStore.ts";
import { XUserTeamStore } from "@Resources/js/modules/teams/Store/XUserTeamStore.ts";
import { XDomainsStore } from "@Resources/js/modules/urls/Store/XDomainsStore.ts";
import { XAuthStore } from "@Resources/js/modules/users/Store/XAuthStore.ts";

export default {
  state: {
    currentSubscription: null,
    futureSubscription: undefined,
    sparkState: window.Spark.state,
    userBillingType: undefined,
    preferredBillingType: undefined,
  },

  getters: {
    isSparkStateLoaded: (state) => state.sparkState instanceof Object,
    getSparkState: (state) => state.sparkState || {},

    isFutureSubscriptionScheduled: (state) => !isNullish(state.futureSubscription),
    getUserFutureSubscription: (state) => state.futureSubscription,

    getPreferredBillingType: (state) => state.preferredBillingType,
    getUserBillingType: (state) => state.userBillingType,

    getUserCurrentSubscription: (state) => state.currentSubscription,

    /**
     * Determines the current subscription group based on the subscription name.
     *
     * @returns {String|null} The subscription group ("pro", "bulk", "qa") or null if not applicable.
     *
     * @remarks WARNING: This condition is biased to only "pro", "bulk", and "qa" plans.
     * If other plans are added in the future, this logic may need to be updated.
     * A more robust solution would involve adding a 'group' attribute to the subscription data model.
     */
    getUserCurrentSubscriptionGroup: (_, getters) => {
      const currentSubscription = getters.getUserCurrentSubscription;

      if (!currentSubscription?.name) {
        return null;
      }

      const groups = ["pro", "bulk", "qa"];

      return groups.find((group) => currentSubscription.name.includes(group)) || null;
    },

    /**
     * Checks if the user is an enterprise customer.
     *
     * @returns {Boolean} True if the user is an enterprise customer, false otherwise.
     *
     * @remarks This getter is very brittle and depends on the naming convention of the subscription.
     * If the naming convention changes, this logic may need to be updated. A more robust solution would
     * involve adding a specific attribute to the subscription data model to indicate enterprise status.
     */
    isUserEnterprise: (_, getters) => {
      return getters.getUserCurrentSubscription?.enterprise === true;
    },

    hasSubscriptionHistory: (state) => {
      return state.sparkState?.user?.has_subscription_history === true;
    },

    /**
     * Gets the highlighted features for the user's current subscription.
     *
     * @returns {Array} The highlighted features if available, or a default fallback.
     */
    currentSubscriptionHighlightedFeatures: (_, getters) => {
      const currentSubscription = getters.getUserCurrentSubscription;

      return currentSubscription?.highlighted ?? [
        "max_aliases_per_month",
        "max_redirects_per_month",
        "max_domains_subdomains",
      ];
    },
  },

  mutations: {
    SET_CURRENT_SUBSCRIPTION: (state, payload) => {
      if (!isNullish(payload)) {
        state.currentSubscription = new BillableSubscription(payload);
      } else {
        state.currentSubscription = null;
      }
    },
    SET_FUTURE_SUBSCRIPTION: (state, subscription) => {
      state.futureSubscription = subscription;
    },
    SET_BILLING_TYPE: (state, { billing, user }) => {
      const currentSubscription = user?.subscriptions?.current?.find((s) => s.type === SUBSCRIPTION_TYPE.DEFAULT);
      const defaultBillingType = billing?.preferred;

      state.userBillingType = currentSubscription?.billing_type ?? defaultBillingType;
      state.preferredBillingType = defaultBillingType;
    },
    SET_SPARK_STATE: (state, payload) => {
      if (payload.user instanceof Array && payload.user.length === 0) {
        payload.user = undefined;
      }

      state.sparkState = payload;
    },
    SET_ALIASES_PER_BILLING_MONTH_OVERAGE_STATUS: (state, payload) => {
      const status = state.sparkState.user.limits.status;
      const update = (status["aliases_per_billing_month"] & ~OVERAGE_STATUS.BILLABLE_ENABLE) | payload;

      Vue.set(status, "aliases_per_billing_month", update);
    },
    UPDATE_STATE_ORGANIZATION_STATUS: (state, payload) => {
      const index = state.sparkState.user.organizations.findIndex(
        (o) => o.slug === payload.slug,
      );

      if (index !== -1) {
        const updatedOrganizations = [...state.sparkState.user.organizations];

        updatedOrganizations[index] = {
          ...state.sparkState.user.organizations[index],
          ...payload,
        };

        state.sparkState.user.organizations = updatedOrganizations;
      }
    },
    UPDATE_USER_PROFILE: (state, payload) => {
      if (state.sparkState?.user) {
        state.sparkState.user = {
          ...state.sparkState.user,
          ...payload,
        };
      }
    },
  },

  actions: {
    refreshState({ commit }, preloadedState = null) {
      const actions = (data) => {
        // Add org and team data to the teams store
        XTeamsStore().initializeFromState(data?.user?.organizations || [], data?.teamsAllowed || false);
        XUserTeamStore().initializeFromState(data?.default_org_slug || null, data?.teamsAllowed || false);
        XDomainsStore().initializeFromState(
          data?.domains?.currentFull || null,
          data?.domains?.available || [],
          data?.domains?.nameservers || [],
        );

        // Normalize empty array to null (API returns [] for guests, but [] is truthy in JS)
        const userData = (Array.isArray(data?.user) && data.user.length === 0) ? null : (data?.user || null);
        XAuthStore()
          .initializeFromState(
            userData,
            !!data?.CCPA,
            !!data?.GDPR,
            data?.locale || "en",
            data.impersonated,
            data?.billing?.preferred ?? null,
            window.Spark?.collectsEuropeanVat === true,
          );

        commit("SET_FUTURE_SUBSCRIPTION", data?.user?.subscriptions?.future?.[0]);
        commit("SET_BILLING_TYPE", data);
        commit("SET_SPARK_STATE", data);
      };

      if (preloadedState) {
        return actions(preloadedState);
      }

      return axios.get(getApiUrl("state")).then(({ data }) => {
        actions(data);
      });
    },
  },
};
